import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  APIProvider,
  Map,
  useMapsLibrary,
  useMap
} from "@vis.gl/react-google-maps"
import { get } from '@util/xhrAPI'
import { fullUpdate } from '@store/slice/sterilizationSlice'
import { updateMapRouteConfig, updateJourneySelectedAddresses } from '@store/slice/pilotSlice'
import Div from '@ui/layout/Div'
import TextType from "@ui/inputs/TextType"
import Buttons from '@ui/elements/Buttons'
import { useTranslation } from 'react-i18next'
import SterilizationCallsListTable from '@scene/window/pilot/__partials/SterilizationCallsListTable'

const ScanJourneyDirections = () => {
  const { calls, activeFilters, seasonsScansData } = useSelector(state => state.sterilization)
  const { mission } = useSelector(state => state.main)
  const { activeSeason } = mission
  const { mapRouteConfig, journeySelectedAddresses } = useSelector(state => state.pilot)
  const dispatch = useDispatch()
  const activeCalls = calls[activeSeason.id]

  const map = useMap()
  const routesLibrary = useMapsLibrary("routes")

  const [directionsService, setDirectionsService] = useState()
  const [directionsRenderer, setDirectionsRenderer] = useState()
  const [routes, setRoutes] = useState([])
  const [routeIndex] = useState(0)
  const selected = routes[routeIndex]
  const leg = selected?.legs[0]

  const renderWayPoints = () => {
    if (activeCalls) {
      const callsArray = Object.values(journeySelectedAddresses) || Object.values(activeCalls)
      return callsArray.map(call => {
        const { position } = call
        return {
          location: position,
          stopover: true
        }
      })
    }
    return []
  }

  const hasActiveFilters = () => {
    let isActiveFilters = false
    for (let prop in activeFilters) {
      if (null !== activeFilters[prop]) {
        isActiveFilters = true
      }
    }
    return isActiveFilters
  }



  // Initialize directions service and renderer
  useEffect(() => {
    if (!routesLibrary || !map) return
    setDirectionsService(new routesLibrary.DirectionsService())
    setDirectionsRenderer(
      new routesLibrary.DirectionsRenderer({
        draggable: true, // Only necessary for draggable markers
        map
      })
    )
  }, [routesLibrary, map])

  useEffect(() => {
    if ('undefined' === typeof activeCalls || hasActiveFilters()) {
      get(`${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/mission-seasons/${activeSeason.id}/calls`, response => {
        const { code, sterilizationCalls, filters, scansData } = response
        if (200 === code) {
          dispatch(
            fullUpdate({
              calls: { ...calls, [activeSeason.id]: { ...sterilizationCalls } },
              filters,
              seasonsScansData: { ...seasonsScansData, [activeSeason.id]: { ...scansData } }
            })
          )
        }
      })
    }
  }, [])

  // Use directions service
  useEffect(() => {
    if (!directionsService || !directionsRenderer || !mapRouteConfig.origin || !mapRouteConfig.destination) return
    directionsService
      .route({
        origin: mapRouteConfig.origin,
        destination: mapRouteConfig.destination,
        drivingOptions: {
          departureTime: new Date(Date.now() + 60000),  // for the time N milliseconds from now.
          trafficModel: 'optimistic'
        },
        region: 'fr',
        waypoints: renderWayPoints(),
        optimizeWaypoints: true,
        // eslint-disable-next-line no-undef
        travelMode: google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: true
      })
      .then(response => {
        directionsRenderer.setDirections(response)
        setRoutes(response.routes)
      })

    return () => directionsRenderer.setMap(map)
  }, [directionsService, directionsRenderer, mapRouteConfig, journeySelectedAddresses])

  // Update direction route
  useEffect(() => {
    if (!directionsRenderer) return
    directionsRenderer.setRouteIndex(routeIndex)
  }, [routeIndex, directionsRenderer])

  if (!leg) return null

  return (
    <Div cssClass='sterilization-calls-directions'>

    </Div>
  )
}

const ScanJourney = () => {
  const dispatch = useDispatch()
  const { mission } = useSelector(state => state.main)
  const { calls } = useSelector(state => state.sterilization)
  const { journeySelectedAddresses } = useSelector(state => state.pilot)
  const activeCalls = calls[mission.activeSeason.id]
  const [t] = useTranslation('common')
  const [origin, setOrigin] = useState(null)
  const [destination, setDestination] = useState(null)

  const validateRoute = () => {
    dispatch(updateMapRouteConfig({
      origin,
      destination
    }))
  }

  const openInGoogleMapsApp = () => {
    const callsArray = Object.values(journeySelectedAddresses) || Object.values(activeCalls)
    const waypointsStr = callsArray.map(call => {
      const { position } = call
      const loc = `${position.lat},${position.lng}`;
      return encodeURIComponent(loc);
    }).join('/');

    const url = `https://www.google.com/maps/dir/${encodeURIComponent(origin)}/${waypointsStr}/${encodeURIComponent(destination)}`;

    window.location.href = url;
  };

  const clearRoute = () => {
    dispatch(updateJourneySelectedAddresses({}))
  }

  const selectAllCalls = () => {
    dispatch(updateJourneySelectedAddresses({ ...calls }))
  }

  return (
    <Div cssClass='sterilization-calls-map'>
      <APIProvider apiKey={import.meta.env.VITE_MAPS_KEY} region='fr' language='fr_FR'>
        <Map
          style={{ width: '100%', height: '500px' }}
          defaultZoom={13}
          fullscreenControl={false}
          defaultCenter={mission.coordinates}
          mapId={`scan-journey-${mission.mairie.name}`}
        >
          <ScanJourneyDirections />
        </Map>
      </APIProvider>

      <Div cssClass='route-plan columns is-multiline'>
        <TextType
          cssClass='column is-6'
          onChange={e => setOrigin(e.target.value)}
          value={origin}
          placeholder={t('labels.start_point')}
          name='route-start'
        />
        <TextType
          cssClass='column is-6'
          onChange={e => setDestination(e.target.value)}
          value={destination}
          placeholder={t('labels.end_point')}
          name='route-destination'
        />

        <Buttons buttons={[
          {
            icon: 'icon-map-pin',
            cssClass: `is-small is-primary ${origin && destination ? '' : 'is-hidden'}`,
            callback: selectAllCalls,
            text: t('actions.select_all_calls')
          },
          { icon: 'icon-floppy-disk', cssClass: 'is-info is-small', callback: () => validateRoute(), text: t('actions.validate') },
          { icon: 'icon-map', cssClass: 'is-info is-small', callback: () => openInGoogleMapsApp(), text: t('actions.open_in_google_maps') },
          { icon: 'icon-gps_off', cssClass: 'is-danger is-small', callback: () => clearRoute(), text: t('actions.cancel') }
        ]} />
      </Div>
      <SterilizationCallsListTable />
    </Div>
  )
}

export default ScanJourney